import StatusTag from '@/components/_common/StatusTag';
import { WorkOrderMigrateConsult } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { workorderComplementColor } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import formatterNumber from '@/utils/number/formatterNumber';
import {
  Card,
  Col,
  Descriptions,
  Divider,
  Modal,
  Row,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import moment from 'moment';
import { useIntl } from 'react-intl';
import MigrationConsultItemsList from './MigrationConsultItemsList';
import { useWorkOrderMigrateConsultItems } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import formatterPrice from '@/utils/number/formatterPrice';

interface Props {
  workOrder?: WorkOrderMigrateConsult;
  opened: boolean;
  onClose: VoidFunction;
}

export default function MigrationConsultDetailModal({
  workOrder,
  ...props
}: Props) {
  const intl = useIntl();

  const workOrderItems = useWorkOrderMigrateConsultItems(
    workOrder?.id,
    !!workOrder?.id,
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let items: Record<string, any> = {
    ['Número']: workOrder?.complementIdentifier,
    ['Status']: workOrder?.status ? (
      <StatusTag
        color={workorderComplementColor[workOrder.status]}
        label={intl.formatMessage({ id: `workOrders.${workOrder.status}` })}
      />
    ) : (
      '-'
    ),
    ['Tipo']: workOrder?.type ? (
      <Tag color="processing">
        {intl.formatMessage({ id: `workOrders.${workOrder.type}` })}
      </Tag>
    ) : (
      '-'
    ),
    ['Empresa']: workOrder?.companyName || '-',
    ['Cliente']: workOrder?.customer || '-',
    ['Veículo']: workOrder?.licensePlate || '-',
    ['KM']: workOrder?.currentKilometers
      ? formatterNumber(workOrder.currentKilometers, {
          minimumFractionDigits: 0,
        })
      : '-',
    ['Prisma']: workOrder?.prisma || '-',
    ['Data de entrada']: workOrder?.entryDate
      ? moment(workOrder.entryDate).format('DD/MM/YYYY HH:mm')
      : '-',
    ['Data de retorno']: workOrder?.returnDate
      ? moment(workOrder.returnDate).format('DD/MM/YYYY HH:mm')
      : '-',
    ['Data de autorização']: workOrder?.authorizationDate
      ? moment(workOrder.authorizationDate).format('DD/MM/YYYY HH:mm')
      : '-',
    ['Previsão de entrega']: workOrder?.estimatedLeaveDate
      ? moment(workOrder.estimatedLeaveDate).format('DD/MM/YYYY HH:mm')
      : '-',
    ['Data de fechamento']: workOrder?.closingDate
      ? moment(workOrder.closingDate).format('DD/MM/YYYY HH:mm')
      : '-',
  };

  if (workOrder?.insurer) {
    items = {
      ...items,
      ['Seguradora']: workOrder.insurer || '-',
      ['Nº do sinistro']: workOrder.accidentNumber || '-',
    };
  }

  items = {
    ...items,
    ['Observação']: workOrder?.observation || '-',
    ['Origem']: workOrder?.origin || '-',
  };

  return (
    <Modal
      width={1100}
      title={`Detalhe do complemento ${workOrder?.complementIdentifier}`}
      open={props.opened}
      onCancel={props.onClose}
      cancelText="Fechar"
      okButtonProps={{ style: { display: 'none' } }}
    >
      <Card
        style={{ borderRadius: 8, marginBottom: 24 }}
        title="Dados do complemento"
      >
        <Descriptions layout="vertical" labelStyle={{ fontWeight: 600 }}>
          {Object.entries(items).map(([label, content], index) => (
            <Descriptions.Item
              label={label}
              style={{ paddingBottom: 0 }}
              key={index}
              contentStyle={{ paddingBottom: 10 }}
            >
              {content}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Card>

      <Typography.Title level={3}>Itens do complemento</Typography.Title>

      <MigrationConsultItemsList
        workOrderItems={workOrderItems.data?.items}
        loading={workOrderItems.isLoading}
      />

      <Divider />

      <MigrationConsultItemsList
        workOrderItems={workOrderItems.data?.items}
        isItemRejectedByCustomer
        loading={workOrderItems.isLoading}
      />

      <Divider />

      <Row justify="end" gutter={[24, 16]}>
        <Col>
          <Statistic
            title="Total de peças"
            value={formatterPrice(workOrderItems.data?.partTotal || 0)}
          />
        </Col>
        <Col>
          <Statistic
            title="Total de serviços"
            value={formatterPrice(workOrderItems.data?.serviceTotal || 0)}
          />
        </Col>
        <Col>
          <Statistic
            title="Total geral"
            value={formatterPrice(workOrderItems.data?.total || 0)}
          />
        </Col>
      </Row>
    </Modal>
  );
}
